import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardBody } from '../components/ui/Card'
import SourceBadge from '../components/sources/SourceBadge'
import SourceLegend from '../components/sources/SourceLegend'
import AboutBlock from '../components/home/AboutBlock'
import { LegalDisclaimer } from '../components/legal'
import { searchAPI } from '../services/api'

// Regex CAS: 2-7 chiffres, tiret, 2 chiffres, tiret, 1 chiffre
const CAS_REGEX = /^\d{2,7}-\d{2}-\d$/

// Type pour les résultats de recherche (API peut retourner différents champs)
interface SearchResult {
  name?: string
  substance_name?: string
  cas_number?: string
  cas?: string
  source: string
  sources_found?: string[]
}

export default function HomePage() {
  const [query, setQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searchError, setSearchError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const navigate = useNavigate()

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedQuery = query.trim()

    if (!trimmedQuery) return

    // Si CAS valide → navigation directe
    if (CAS_REGEX.test(trimmedQuery)) {
      navigate(`/substance/${trimmedQuery}`)
      return
    }

    // Sinon → recherche par nom via API
    setIsSearching(true)
    setSearchError(null)
    setHasSearched(true)

    try {
      const response = await searchAPI.search(trimmedQuery, ['inrs', 'anses', 'echa', 'agritox'])

      // Extraire les résultats de toutes les sources
      const results: SearchResult[] = []
      const seenCAS = new Set<string>()

      if (response && typeof response === 'object' && 'sources' in response) {
        const sources = (response as { sources: Record<string, { data?: Array<Record<string, unknown>> }> }).sources

        for (const [sourceKey, sourceData] of Object.entries(sources)) {
          if (sourceData?.data && Array.isArray(sourceData.data)) {
            for (const item of sourceData.data) {
              const cas = (item.cas_number || item.cas) as string | undefined
              const itemName = (item.name || item.substance_name || cas) as string
              if (cas && !seenCAS.has(cas)) {
                seenCAS.add(cas)
                results.push({
                  name: itemName,
                  cas_number: cas,
                  source: sourceKey,
                  sources_found: [sourceKey]
                })
              } else if (cas && seenCAS.has(cas)) {
                // Ajouter la source au résultat existant
                const existing = results.find(r => r.cas_number === cas)
                if (existing && !existing.sources_found?.includes(sourceKey)) {
                  existing.sources_found?.push(sourceKey)
                }
              }
            }
          }
        }
      }

      setSearchResults(results.slice(0, 10))
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur de recherche'
      if (message.includes('PAYWALL') || message.includes('quota')) {
        setSearchError('Quota de recherches atteint. Connectez-vous pour continuer.')
      } else {
        setSearchError(message)
      }
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleResultClick = (cas: string) => {
    navigate(`/substance/${cas}`)
  }

  const quickExamples = [
    { name: 'Benzène', cas: '71-43-2' },
    { name: 'Formaldéhyde', cas: '50-00-0' },
    { name: 'Toluène', cas: '108-88-3' },
    { name: 'Glyphosate', cas: '1071-83-6' },
  ]

  // Sources officielles avec liens
  const sources = ['inrs', 'anses', 'echa', 'agritox'] as const

  return (
    <div className="space-y-4">
      {/* Description factuelle (titre dans le Header) */}
      <div className="text-center py-3">
        <p className="text-sm text-gray-600 max-w-2xl mx-auto leading-relaxed">
          Centralisez vos recherches toxicologiques à partir de sources réglementaires officielles.
          Chaque fiche est construite à partir d'un numéro CAS et regroupe les valeurs de référence,
          classifications et informations de prévention disponibles.
        </p>
      </div>

      {/* Search - compact */}
      <Card className="max-w-xl mx-auto">
        <CardBody className="p-4">
          <form onSubmit={handleSearch} className="space-y-3">
            <div>
              <label
                htmlFor="search"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Rechercher une substance
              </label>
              <input
                type="text"
                id="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Benzène, formaldéhyde, 71-43-2..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isSearching}
              />
            </div>
            <button
              type="submit"
              className="btn-primary w-full py-2 flex items-center justify-center gap-2"
              disabled={isSearching || !query.trim()}
            >
              {isSearching ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Recherche...
                </>
              ) : (
                'Rechercher'
              )}
            </button>
          </form>
        </CardBody>
      </Card>

      {/* Search Results */}
      {hasSearched && !isSearching && (
        <div className="max-w-xl mx-auto">
          {searchError ? (
            <Card className="border-red-200 bg-red-50">
              <CardBody className="text-center py-6">
                <p className="text-red-600">{searchError}</p>
              </CardBody>
            </Card>
          ) : searchResults.length > 0 ? (
            <div className="space-y-3">
              {/* Compteur et légende des sources */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p className="text-sm text-gray-600">
                  {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} pour « {query} »
                </p>
                <SourceLegend className="text-xs" />
              </div>

              <Card>
                <CardBody className="p-0">
                  <div className="divide-y divide-gray-100">
                  {searchResults.map((result) => (
                    <button
                      key={result.cas_number || result.name}
                      onClick={() => result.cas_number && handleResultClick(result.cas_number)}
                      className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left flex items-center justify-between gap-4"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">
                          {result.name || result.cas_number}
                        </p>
                        <p className="text-sm text-gray-500">
                          CAS {result.cas_number || '—'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {result.sources_found?.map((src) => (
                          <span
                            key={src}
                            className={`w-2 h-2 rounded-full ${
                              src === 'inrs' ? 'bg-orange-500' :
                              src === 'anses' ? 'bg-green-600' :
                              src === 'echa' ? 'bg-blue-600' :
                              src === 'agritox' ? 'bg-emerald-500' :
                              'bg-gray-400'
                            }`}
                            title={src.toUpperCase()}
                          />
                        ))}
                        <svg className="w-4 h-4 text-gray-400 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              </CardBody>
            </Card>
            </div>
          ) : (
            <Card className="border-gray-200 bg-gray-50">
              <CardBody className="text-center py-6">
                <p className="text-gray-600">Aucun résultat pour « {query} »</p>
                <p className="text-sm text-gray-500 mt-1">Essayez un autre terme ou un numéro CAS</p>
              </CardBody>
            </Card>
          )}

          {/* Disclaimer légal sous les résultats (CDC v1.2 §9.3) */}
          <LegalDisclaimer variant="search" className="mt-4" />
        </div>
      )}

      {/* Exemples + Sources officielles + Bloc À propos */}
      {!hasSearched && (
        <div className="max-w-xl mx-auto space-y-4">
          {/* Exemples cliquables */}
          <div className="flex flex-wrap justify-center items-center gap-2">
            <span className="text-xs text-gray-500">Exemples :</span>
            {quickExamples.map((example) => (
              <button
                key={example.cas}
                onClick={() => navigate(`/substance/${example.cas}`)}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-sm transition-colors"
              >
                {example.name}
              </button>
            ))}
          </div>

          {/* Sources officielles - logos cliquables */}
          <div id="sources-officielles" className="text-center">
            <p className="text-xs text-gray-500 mb-3">Sources officielles</p>
            <div className="flex justify-center items-center gap-8">
              {sources.map((source) => (
                <SourceBadge
                  key={source}
                  sourceKey={source}
                  size="xl"
                  showLink={true}
                  variant="compact"
                />
              ))}
            </div>
          </div>

          {/* Bloc À propos de RISK-DATA (CDC v1.2 §9.2) */}
          <AboutBlock />
        </div>
      )}
    </div>
  )
}
